import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Printer, Download, Eye, History, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { money, shortDate, balanceLabel } from "@/lib/format";
import { Button } from "@/components/ui-kit/Button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui-kit/Card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Pill } from "@/components/ui-kit/Pill";
import { StatusBadge } from "@/components/ui-kit/StatusBadge";
import { PaymentStatusPill } from "@/components/payments/PaymentStatusPill";
import { LedgerStatement } from "@/components/ledger/LedgerStatement";
import { PdfPreviewDialog } from "@/components/pdf/PdfPreviewDialog";
import { useFunder } from "@/funder/FunderContext";

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="truncate text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

/**
 * STRICTLY READ-ONLY complete record for a matter (dispute/audit failsafe).
 * Shows header, time entries, invoices (with downloadable frozen PDFs), payments,
 * the reconciliation statement, and the audit log — with NO action controls.
 */
export function MatterRecordPage() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { abbr, typeLabel } = useFunder();
  const [matter, setMatter] = useState(null);
  const [clientName, setClientName] = useState("");
  const [statement, setStatement] = useState(null);
  const [entries, setEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [dl, setDl] = useState(null);

  useEffect(() => {
    api.get(`/matters/${matterId}`).then((r) => {
      setMatter(r.data);
      api.get(`/clients/${r.data.client_id}`).then((c) => setClientName(c.data.name)).catch(() => {});
    }).catch(() => {});
    api.get(`/matters/${matterId}/statement`).then((r) => setStatement(r.data)).catch(() => {});
    api.get(`/matters/${matterId}/time-entries`).then((r) => setEntries(r.data)).catch(() => {});
    api.get(`/matters/${matterId}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
    api.get(`/matters/${matterId}/payments`).then((r) => setPayments(r.data)).catch(() => {});
    api.get(`/matters/${matterId}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  }, [matterId]);

  const downloadInvoice = async (id) => {
    setDl(id);
    try { await downloadFile(`/invoices/${id}/pdf`); } finally { setDl(null); }
  };

  if (!matter) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const isAcsa = matter.type === "ACSA_Client";
  const r1 = (matter.coverage_rounds && matter.coverage_rounds[0]) || {};
  const levels = [["l1", r1.l1_enabled], ["l2", r1.l2_enabled], ["l3", r1.l3_enabled]];
  const orderedInvoices = invoices.slice().sort((a, b) => (b.issue_date || "").localeCompare(a.issue_date || ""));

  return (
    <div className="space-y-6" data-testid="matter-record-page">
      <div className="flex flex-wrap items-start justify-between gap-4 print:hidden">
        <button onClick={() => navigate(`/clients-matters/${matter.client_id}`)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="record-back">
          <ArrowLeft className="h-4 w-4" /> Back to client
        </button>
        <Button variant="secondary" onClick={() => window.print()} data-testid="record-print">
          <Printer className="h-4 w-4" /> Print
        </Button>
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">{matter.name}</h1>
          <Pill tone={matter.status === "Open" ? "success" : "muted"}>{matter.status}</Pill>
          <Pill tone="accent" data-testid="record-readonly-badge"><Lock className="mr-1 h-3 w-3" /> Read-only record</Pill>
        </div>
        <p className="mt-1.5 text-sm text-muted-foreground">{clientName} · complete dispute/audit record. View, download, and print only — nothing here can be edited.</p>
      </div>

      {/* Header / matter details */}
      <Card data-testid="record-header">
        <CardHeader><CardTitle>Matter details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
            <Field label="Client">{clientName}</Field>
            <Field label="Type">{typeLabel(matter.type)}</Field>
            <Field label="Rate">{money(matter.hourly_rate)}/hr</Field>
            <Field label="Increment">{matter.billing_increment_min} min</Field>
            <Field label="Opened">{shortDate(matter.open_date)}</Field>
            <Field label="Closed">{matter.close_date ? shortDate(matter.close_date) : "—"}</Field>
            <Field label={`${abbr} coverage`}>
              {isAcsa ? (
                <span className="inline-flex gap-1">
                  {levels.map(([k, on]) => (
                    <span key={k} className={on ? "rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase bg-status-paid/15 text-status-paid" : "rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase bg-secondary text-muted-foreground/50"}>{k}</span>
                  ))}
                </span>
              ) : "—"}
            </Field>
            {isAcsa && <Field label="L1 free / L2 cap">{matter.l1_free_hours}h / {money(matter.l2_cap)}</Field>}
            <Field label="Final client balance">{statement ? balanceLabel(statement.client?.running_balance || 0) : "…"}</Field>
            {isAcsa && <Field label={`Final ${abbr} balance`}>{statement ? balanceLabel(statement.acsa?.running_balance || 0) : "…"}</Field>}
          </div>
        </CardContent>
      </Card>

      {/* Time entries */}
      <Card data-testid="record-time-entries">
        <CardHeader><CardTitle>Time entries</CardTitle></CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No time entries.</p>
          ) : (
            <Table>
              <THead><TR><TH>Date</TH><TH>Service</TH><TH className="text-right">Hours</TH><TH className="text-right">Amount</TH><TH>Status</TH></TR></THead>
              <TBody>
                {entries.map((e) => (
                  <TR key={e.id} data-testid={`record-entry-${e.id}`}>
                    <TD className="whitespace-nowrap text-muted-foreground">{shortDate(e.date)}</TD>
                    <TD className="text-foreground">{e.description || "—"}</TD>
                    <TD className="nums text-right text-muted-foreground">{e.decimal_hours}</TD>
                    <TD className="nums text-right text-foreground">{money(e.billable_amount || 0)}</TD>
                    <TD><Pill tone={e.status === "Approved" ? "success" : e.status === "Excluded" ? "muted" : "info"}>{e.status}</Pill></TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invoices with downloadable frozen PDFs */}
      <Card data-testid="record-invoices">
        <CardHeader><CardTitle>Invoices</CardTitle></CardHeader>
        <CardContent className="p-0">
          {orderedInvoices.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No invoices issued.</p>
          ) : (
            <Table>
              <THead><TR><TH>Number</TH><TH>Payer</TH><TH>Issued</TH><TH className="text-right">Amount due</TH><TH>Status</TH><TH className="text-right">PDF</TH></TR></THead>
              <TBody>
                {orderedInvoices.map((i) => (
                  <TR key={i.id} data-testid={`record-invoice-${i.id}`}>
                    <TD className="font-medium text-foreground">{i.number || "Draft"}</TD>
                    <TD className="text-muted-foreground">{i.payer === "ACSA" ? abbr : "Client"}</TD>
                    <TD className="text-muted-foreground">{i.issue_date ? shortDate(i.issue_date) : "—"}</TD>
                    <TD className="nums text-right">{i.snapshot ? money(i.snapshot.amount_due) : "—"}</TD>
                    <TD><StatusBadge status={i.status} /></TD>
                    <TD className="text-right">
                      {i.snapshot && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setPreviewInvoiceId(i.id)} data-testid={`record-preview-invoice-${i.id}`}>
                            <Eye className="h-3.5 w-3.5" /> Preview
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadInvoice(i.id)} disabled={dl === i.id} data-testid={`record-download-invoice-${i.id}`}>
                            {dl === i.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                          </Button>
                        </div>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payments (read-only — no void) */}
      <Card data-testid="record-payments">
        <CardHeader><CardTitle>Payments</CardTitle></CardHeader>
        <CardContent className="p-0">
          {payments.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No payments recorded.</p>
          ) : (
            <Table>
              <THead><TR><TH>Date</TH><TH>Payer</TH><TH className="text-right">Amount</TH><TH>Method</TH><TH>Reference</TH><TH>Status</TH></TR></THead>
              <TBody>
                {payments.map((p) => (
                  <TR key={p.id} data-testid={`record-payment-${p.id}`}>
                    <TD className="text-muted-foreground">{shortDate(p.date)}</TD>
                    <TD className="text-muted-foreground">{p.payer === "ACSA" ? abbr : "Client"}</TD>
                    <TD className="nums text-right font-medium text-foreground">{money(p.amount)}</TD>
                    <TD className="text-muted-foreground">{p.method}</TD>
                    <TD className="text-muted-foreground">{p.reference || "—"}</TD>
                    <TD><PaymentStatusPill status={p.status} /></TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reconciliation statement */}
      <Card data-testid="record-statement">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Reconciliation statement</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/matters/${matterId}/statement`)} data-testid="record-open-statement">
            <Receipt className="h-4 w-4" /> Open full statement
          </Button>
        </CardHeader>
        <CardContent>
          <LedgerStatement matterId={matterId} />
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card data-testid="record-audit">
        <CardHeader className="flex flex-row items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <CardTitle>Audit log</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No activity recorded.</p>
          ) : (
            <div className="space-y-2">
              {audit.map((a) => (
                <div key={a.id} className="border-l-2 border-border pl-3" data-testid={`record-audit-${a.id}`}>
                  <p className="text-sm text-foreground">{a.message}</p>
                  <p className="text-xs text-muted-foreground">{shortDate(a.timestamp)} · {a.actor || "system"}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <PdfPreviewDialog
        open={previewInvoiceId !== null}
        onOpenChange={(o) => { if (!o) setPreviewInvoiceId(null); }}
        path={previewInvoiceId ? `/invoices/${previewInvoiceId}/pdf` : ""}
        title="Invoice preview"
      />
    </div>
  );
}

export default MatterRecordPage;
